import { Link, useActionData, useNavigation, useParams, useSearchParams } from "react-router";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import type { QueueItem, SpaceEvent } from "@repo/shared-types";
import { api } from "@/lib/api";
import { errorMessage } from "@/lib/session";
import { loadSpaceContext, useSpaceContext } from "@/lib/events";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { divisionLabel, levelLabel } from "@/lib/results";
import { tenantPath } from "@/lib/tenants";
import { formatMoment } from "@/lib/times";
import { toast } from "@/lib/toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyNote } from "@/components/empty-note";
import { BadgeCheck } from "lucide-react";
import { ErrorNote } from "@/components/form";
import { HideResult, VerifyResult } from "@/components/leaderboard";
import { ItemBadge, ReviewItem } from "@/components/review-item";
import { BackLink } from "@/components/navigation";
import { PageTitle } from "@/components/brand";

/**
 * An event's verification queue: every shared result waiting for the owner
 * or an admin holding verify_scores, oldest first, a page at a time.
 *
 * A result waits for one of two reasons — late, and off its leaderboard
 * until it is verified; or unverified, on a verifiable part — said once at
 * the top of the page, a line for each reason the page holds, with each
 * item carrying its reason's badge (decided with Tiago 12 October: the
 * sentence under every item drowned the items). When an edit made its
 * verification stale, an item says what was verified and by whom.
 *
 * Verifying sends back when the result last changed, as this page showed it,
 * so a result edited in between is refused rather than verified unseen. Whoever
 * also moderates can hide a result on its part's board from here.
 *
 * Anybody else is told whose page this is; the api refuses them the queue,
 * whatever this page renders.
 */

export async function verifyLoader({ request, params }: LoaderFunctionArgs) {
  const slug = params.slug ?? "";
  const eventId = params.event ?? "";
  const cursor = new URL(request.url).searchParams.get("cursor");
  const [context] = await Promise.all([loadSpaceContext(request, slug), read(queries.event(slug, eventId))]);
  if (context.canVerify) await read(queries.queue(slug, eventId, { cursor }));
  return null;
}

/**
 * What the action answers: nothing once it has worked — a toast says so —
 * or why not, which stays on the page. Never undefined: hiding's sheet
 * closes on an answer without an error.
 */
type ActionResult = { error?: string };

export async function verifyAction({ request, params }: ActionFunctionArgs): Promise<ActionResult> {
  const form = await request.formData();
  const events = api.space(params.slug ?? "").events;
  const eventId = params.event ?? "";
  const part = String(form.get("part") ?? "");
  const entry = String(form.get("entry") ?? "");
  // A team's, by its id, in place of an athlete's.
  const team = String(form.get("team") ?? "");
  try {
    switch (String(form.get("intent") ?? "")) {
      case "verify": {
        const changedAt = String(form.get("changedAt") ?? "");
        if (team) await events.teams.verify(eventId, team, { changedAt });
        else await events.verify(eventId, part, entry, { changedAt });
        toast("Verified.");
        return {};
      }
      case "unverify":
        if (team) await events.teams.unverify(eventId, team);
        else await events.unverify(eventId, part, entry);
        toast("Verification taken back.");
        return {};
      case "hide":
        if (team) await events.teams.hide(eventId, team);
        else await events.hide(eventId, part, entry);
        toast("Hidden.");
        return {};
      default:
        return { error: "Unknown action" };
    }
  } catch (error) {
    return { error: errorMessage(error, "Something went wrong") };
  }
}

/**
 * Why a result waits, as the api names it: each reason's badge, and its one
 * explanation at the top. A reason this build does not know shows the api's
 * word as its badge and explains nothing it could get wrong.
 */
const REASONS: Record<string, { badge: string; why: string }> = {
  late: {
    badge: "Late",
    why: "Changed or shared after its part's deadline: off the leaderboard until it is verified.",
  },
  unverified: {
    badge: "Unverified",
    why: "On a verifiable part: shown on the leaderboard, marked, until it is verified.",
  },
};

export default function VerifyPage() {
  const params = useParams();
  const slug = params.slug ?? "";
  const eventId = params.event ?? "";
  const { tenant, canVerify, canModerate, zone } = useSpaceContext(slug);
  const { data: event } = useSuspenseQuery(queries.event(slug, eventId));
  const cursor = useSearchParams()[0].get("cursor");
  // Read by the loader for those who verify; nobody else asks.
  const queue = useQuery({ ...queries.queue(slug, eventId, { cursor }), enabled: canVerify }).data ?? null;
  const result = useActionData() as ActionResult | undefined;
  const busy = useNavigation().state === "submitting";
  const back = `${tenantPath(tenant)}/events/${event.id}`;
  const reasons = Object.keys(REASONS).filter((reason) => queue?.items.some((item) => item.reason === reason));

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <BackLink to={back}>{event.title}</BackLink>
        <PageTitle of={["Verify results", event.title]} />
        <h1 className="page-title">Verify results</h1>
        {queue && <p className="text-muted-foreground">{queue.waiting === 1 ? "1 waiting" : `${queue.waiting} waiting`}</p>}
      </div>

      {result?.error && <ErrorNote message={result.error} />}

      {!canVerify || !queue ? (
        <Alert role="note">
          <AlertDescription>
            Only the owner, and admins the owner has given Verify scores, verify results here.
          </AlertDescription>
        </Alert>
      ) : queue.items.length === 0 && !cursor ? (
        <EmptyNote icon={BadgeCheck}>Nothing is waiting for verification.</EmptyNote>
      ) : (
        <>
          {reasons.length > 0 && (
            <ul aria-label="Why they wait" className="space-y-2 text-sm text-muted-foreground">
              {reasons.map((reason) => (
                <li key={reason} className="flex items-start gap-2">
                  <ItemBadge>{REASONS[reason]!.badge}</ItemBadge>
                  <span>{REASONS[reason]!.why}</span>
                </li>
              ))}
            </ul>
          )}
          <Card className="gap-0 py-0">
            <ol className="divide-y divide-border" aria-label="Waiting">
              {queue.items.map((item) => (
                <Item
                  key={item.team ? item.team.id : `${item.entryId}-${item.partId}`}
                  item={item}
                  event={event}
                  eventPath={back}
                  zone={zone}
                  canModerate={canModerate}
                  busy={busy}
                />
              ))}
            </ol>
          </Card>
        </>
      )}

      {queue && (cursor || queue.nextCursor) && (
        <nav className="flex justify-between" aria-label="Pages">
          {cursor ? (
            <Button asChild variant="outline" size="lg">
              <Link to="?">← First</Link>
            </Button>
          ) : (
            <span />
          )}
          {queue.nextCursor && (
            <Button asChild variant="outline" size="lg">
              <Link to={`?cursor=${encodeURIComponent(queue.nextCursor)}`}>Next →</Link>
            </Button>
          )}
        </nav>
      )}
    </section>
  );
}

/**
 * One result waiting: its part and board with why it waits, the athlete or
 * the team with the result as it stands, when it changed or was shared, what
 * was verified before when that went stale, and Verify — then Hide…, for
 * whoever moderates — in the same place on every item.
 */
function Item({
  item,
  event,
  eventPath,
  zone,
  canModerate,
  busy,
}: {
  item: QueueItem;
  event: SpaceEvent;
  eventPath: string;
  zone: string;
  canModerate: boolean;
  busy: boolean;
}) {
  const part = event.parts.find((candidate) => candidate.id === item.partId);
  const team = item.team;
  // The later of the two is what made it wait. Compared as moments: the
  // clock comes to the microsecond, the share to the second.
  const shared = Date.parse(item.sharedAt) > Date.parse(item.changedAt);
  const previous = item.previous;

  return (
    <ReviewItem
      event={event}
      item={item}
      badge={<ItemBadge>{REASONS[item.reason]?.badge ?? item.reason}</ItemBadge>}
      teamLink={team ? `${eventPath}/teams/${team.id}` : undefined}
      when={
        <>
          {team ? "a team, " : ""}
          {shared ? (team ? "on the board" : "shared") : "changed"} {formatMoment(shared ? item.sharedAt : item.changedAt, zone)}
          {item.reason === "late" && part?.deadline ? `, after the deadline (${formatMoment(part.deadline, zone)})` : ""}
        </>
      }
      actions={
        <>
          <VerifyResult
            intent="verify"
            label="Verify"
            variant="default"
            partId={item.partId}
            entryId={item.entryId}
            teamId={team?.id}
            changedAt={item.changedAt}
            busy={busy}
          />
          {canModerate && <HideResult partId={item.partId} entryId={item.entryId} teamId={team?.id} busy={busy} />}
        </>
      }
    >
      {previous && (
        <p className="text-xs text-muted-foreground">
          Changed after verification: it was verified as{" "}
          {[
            previous.scores.map((score) => score.display).filter(Boolean).join(", then "),
            previous.level ? levelLabel(previous.level) : "",
            previous.division ? divisionLabel(previous.division) : "",
          ]
            .filter(Boolean)
            .join(" · ")}
          {previous.verifiedBy ? ` by @${previous.verifiedBy.username}` : ""}, {formatMoment(previous.verifiedAt, zone)}.
        </p>
      )}
    </ReviewItem>
  );
}
